import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  api,
  type NotificationRow,
  type SavedViewSummary,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { prefs } from "../../lib/prefs";
import { timeAgo } from "../../lib/format";
import { useDismiss } from "../ui";

/**
 * The application shell: a 52px dark header, a sidebar that collapses to an
 * icon rail, and one scrolling content pane.
 *
 * The shell occupies exactly the viewport and never scrolls. Everything that
 * can grow — the nav, the content, a long table — scrolls inside its own box,
 * so the header and the nav are always where the reader left them.
 *
 * Geometry and colour follow the v2 prototype, with one deliberate departure:
 * the prototype's breadcrumb band is gone. It spent 41px of every page saying
 * "Compendium / Ingestion" while the sidebar already highlighted Ingestion.
 *
 * Navigation is filtered by capability here; screens are still guarded
 * independently by the router, because hiding a link is a courtesy and not a
 * permission.
 */

type NavEntry = {
  to: string;
  label: string;
  icon: string;
  icon2?: string;
  requires?: (u: ReturnType<typeof useAuth>["user"]) => boolean;
  badge?: (counts: Counts) => number;
};

type Counts = { tasks: number };

const NAV: NavEntry[] = [
  { to: "/", label: "Home", icon: "M3 10.5 12 3l9 7.5", icon2: "M5.5 9.5V21h13V9.5" },
  {
    to: "/assistant",
    label: "Discover",
    icon: "M21 11.5a8.4 8.4 0 0 1-9 8.4L3 21l1.1-4.3A8.4 8.4 0 1 1 21 11.5Z",
  },
  {
    to: "/documents",
    label: "Documents",
    icon: "M6 3h8l4 4v14H6z",
    icon2: "M14 3v4h4",
  },
  {
    to: "/tasks",
    label: "My Tasks",
    icon: "M9 11.5l2.5 2.5L18 7.5",
    icon2: "M20 12a8 8 0 1 1-4.5-7.2",
    requires: (u) => !!u && (u.canReview || u.canUpload),
    badge: (c) => c.tasks,
  },
  {
    to: "/ingestion",
    label: "Ingestion",
    icon: "M12 16V4m0 0L8 8m4-4 4 4",
    icon2: "M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2",
    requires: (u) => !!u?.canUpload,
  },
  {
    to: "/workflows",
    label: "Workflows",
    icon: "M4 5h6v6H4zM14 13h6v6h-6z",
    icon2: "M10 8h4v8",
    requires: (u) => !!u?.canUpload,
  },
  {
    to: "/users",
    label: "Users & Roles",
    icon: "M16 19v-1a4 4 0 0 0-8 0v1",
    icon2: "M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6",
    requires: (u) => !!u?.canManage,
  },
  {
    to: "/audit",
    label: "Audit Log",
    icon: "M8 4h8l3 3v13H5V7z",
    icon2: "M8 12h8M8 16h5",
    requires: (u) => !!u && (u.canManage || u.canPublish),
  },
  {
    to: "/admin",
    label: "Admin panel",
    icon: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6",
    icon2: "M19.1 14.4a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1 2 2 0 1 1-4 0 1.6 1.6 0 0 0-2.7-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7 2 2 0 1 1 0-4 1.6 1.6 0 0 0 1.1-2.7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1 2 2 0 1 1 4 0 1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7 2 2 0 1 1 0 4 1.6 1.6 0 0 0-1.4 1Z",
    requires: (u) => !!u?.canManage,
  },
];

// Library dot colours, from the prototype's saved-view palette. The backend
// sends its own per-view colour; this is the fallback.
const VIEW_DOT: Record<string, string> = {
  published: "#8DE971",
  awaiting_approval: "#AD96DC",
  awaiting_review: "#ECF166",
  review_due: "#FF7276",
};

function NavIcon({ path, path2, opacity }: { path: string; path2?: string; opacity: number }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity }}
      className="shrink-0"
      aria-hidden
    >
      <path d={path} />
      {path2 && <path d={path2} />}
    </svg>
  );
}

export function Shell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  // Read from storage on the first render rather than in an effect, so a
  // reader who collapsed the rail does not watch it snap shut after paint.
  // It lives here rather than in the nav because the content pane below
  // remounts on every navigation and would take the state with it.
  const [collapsed, setCollapsed] = useState(prefs.sidebarCollapsed);

  // The one screen that manages its own scrolling: a transcript beside a
  // rail, where a shared scroller leaves a band of empty rail next to the
  // conversation. Listed here rather than signalled from the screen because
  // the wrapper it needs to change is above the outlet.
  const paneIsFixedHeight = location.pathname.startsWith("/assistant");

  const toggleSidebar = () =>
    setCollapsed((current) => {
      prefs.setSidebarCollapsed(!current);
      return !current;
    });

  const { data: views } = useQuery({
    queryKey: ["views"],
    queryFn: async () =>
      (await api.get<{ views: SavedViewSummary[] }>("/documents", { pageSize: 1 }))
        .views,
    staleTime: 30_000,
  });

  const { data: taskCount } = useQuery({
    queryKey: ["task-count"],
    queryFn: async () => {
      const queue = await api.get<{ items: unknown[] }>("/workflow/tasks");
      return queue.items.length;
    },
    enabled: !!user && (user.canReview || user.canUpload),
    staleTime: 30_000,
  });

  const counts: Counts = { tasks: taskCount ?? 0 };
  const allowed = NAV.filter((entry) => !entry.requires || entry.requires(user));

  /**
   * Dual-mode header search, exactly as the prototype defines it: a question
   * goes to the assistant, anything else to search. The heuristic is theirs.
   */
  const onSearchKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    const value = query.trim();
    if (!value) return;
    const looksLikeQuestion =
      /\?$/.test(value) ||
      /^(what|who|when|where|why|how|can|do|does|is|are|should)\b/i.test(value);
    navigate(
      looksLikeQuestion
        ? `/assistant?q=${encodeURIComponent(value)}`
        : `/search?q=${encodeURIComponent(value)}`,
    );
    setQuery("");
  };

  // The shell is exactly the viewport and never scrolls: long content scrolls
  // inside the pane that owns it, so the header and the nav stay put.
  //
  // `min-h-screen` had to come off the root. It is a floor, and on mobile
  // `100vh` is the large viewport (toolbars retracted) while `height: 100%`
  // tracks the current one — so it made the root taller than the visible area
  // whatever the panes did. That, plus the uncapped sidebar, were the two
  // reasons the page scrolled.
  //
  // `overflow-clip` rather than `overflow-hidden`: `hidden` would make the root
  // a scroll container, so focusing anything that had ended up outside the box
  // would scroll the header away with no scrollbar to bring it back.
  return (
    <div className="flex h-full flex-col overflow-clip">
      {/* ------------------------------------------------------------ header */}
      {/* `relative`, not `sticky`: with no document scroll there is nothing to
          stick to, but `z-index` only applies to positioned elements, and
          dropping the positioning entirely would silently drop `z-30` and put
          the notification dropdown in the same stacking context as the content
          pane's popovers. */}
      <header className="relative z-30 flex h-header shrink-0 items-center gap-4 bg-ink px-4 text-pearl">
        {/* Left of the wordmark, over the rail it controls, so the association
            is positional and needs no label. */}
        <button
          onClick={toggleSidebar}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
          title={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
          className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-nav text-night-50 transition-colors hover:bg-night-600 hover:text-pearl md:flex"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M9 4v16" />
            {/* The chevron points where the panel will go. */}
            <path d={collapsed ? "m13 10 2.5 2-2.5 2" : "m17 10-2.5 2 2.5 2"} />
          </svg>
        </button>

        <NavLink to="/" className="flex shrink-0 items-center gap-[9px] no-underline">
          <img
            src="/assets/nxzen-logomark.jpg"
            alt="nxzen"
            className="h-[26px] w-auto rounded-chip"
          />
          <span className="h-4 w-px bg-night-450" aria-hidden />
          <span className="whitespace-nowrap text-base+ text-night-50">
            Compendium DMS
          </span>
        </NavLink>

        <div className="ml-3 flex h-8 min-w-0 max-w-[560px] flex-1 items-center gap-2 rounded-field border border-night-500 bg-night-800 px-[11px]">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#7A737F"
            strokeWidth="2"
            strokeLinecap="round"
            className="shrink-0"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Search documents or ask a question…"
            aria-label="Search documents or ask a question"
            className="min-w-0 flex-1 border-0 bg-transparent text-base+ text-pearl outline-none"
          />
          <span className="hidden shrink-0 rounded-[4px] border border-night-500 px-[5px] py-[2px] text-2xs font-extrabold tracking-[0.08em] text-muted lg:inline">
            ⏎ SEARCH · ? ASKS AI
          </span>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-[7px]">
          <NotificationBell />
          {user && (
            <>
              <span
                className="grid h-[27px] w-[27px] place-items-center rounded-full text-sm font-extrabold text-ink"
                style={{ background: user.avatarColour }}
                title={`${user.displayName} · ${user.roleLabel}`}
              >
                {user.initials}
              </span>
              <button
                onClick={() => void signOut()}
                className="p-1 text-night-200 transition-colors hover:text-pearl"
                aria-label="Sign out"
                title="Sign out"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M15 17l5-5-5-5M20 12H9M12 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
                </svg>
              </button>
            </>
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* --------------------------------------------------------- sidebar */}
        <nav
          className={`hidden shrink-0 flex-col border-r border-line bg-surface px-[10px] py-3 transition-[width] duration-200 md:flex ${
            collapsed ? "w-[56px]" : "w-sidebar"
          }`}
        >
          {/* The nav list and the Library views scroll; the System card is a
              sibling, not a child. `mt-auto` used to pin that card, but an auto
              margin only absorbs *positive* free space — the moment the nav
              overflowed it resolved to zero and the card spilled past the
              bottom edge, taking the page with it. Making the scroller the
              flexible item and the card `shrink-0` pins it either way.

              The card also has to stay outside the scroller because its info
              popover is wider than the rail, and `overflow-y: auto` forces
              `overflow-x` to `auto` too — inside, the popover would be clipped
              and would add a horizontal scrollbar. */}
          <div
            className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto overscroll-contain"
            tabIndex={0}
          >
          <div className="flex flex-col gap-px">
            {allowed.map((entry) => {
              const badge = entry.badge?.(counts) ?? 0;
              return (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === "/"}
                  // A native tooltip rather than a styled popover: an
                  // absolutely positioned one would be clipped at 56px, because
                  // the scroller's `overflow-y: auto` forces `overflow-x` to
                  // `auto` as well. "Scroll vertically, overflow horizontally"
                  // is not expressible in CSS.
                  title={collapsed ? entry.label : undefined}
                  // Active is a soft fill with an accent rule down the
                  // left, not a full-width slab of ink. The ink pill is the
                  // primary-button treatment, and using it here made the one
                  // page you are already on the loudest thing in the frame --
                  // a nav should tell you where you are, quietly.
                  className={({ isActive }) =>
                    [
                      "relative flex items-center gap-[10px] rounded-nav py-[7px] text-md no-underline transition-colors",
                      collapsed ? "justify-center px-0" : "px-[10px]",
                      isActive
                        ? "bg-surface-subtle font-bold text-ink"
                        : "font-semibold text-muted-strong hover:bg-surface-subtle hover:text-ink",
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className={`absolute rounded-full bg-accent-deep ${
                            collapsed
                              ? "left-0 top-1/2 h-[18px] w-[3px] -translate-y-1/2"
                              : "left-0 top-[7px] h-[calc(100%-14px)] w-[3px]"
                          }`}
                          aria-hidden
                        />
                      )}
                      <NavIcon
                        path={entry.icon}
                        path2={entry.icon2}
                        opacity={isActive ? 1 : 0.6}
                      />
                      {!collapsed && <span className="flex-1">{entry.label}</span>}
                      {badge > 0 && !collapsed && (
                        <span
                          className="rounded-full px-[6px] py-px text-xs font-extrabold"
                          style={{ background: "#FCF6D2", color: "#75650B" }}
                        >
                          {badge}
                        </span>
                      )}
                      {/* Collapsed, the count has nowhere to sit, but "you have
                          tasks waiting" is the one thing worth not losing — so
                          it becomes a dot on the icon. */}
                      {badge > 0 && collapsed && (
                        <span
                          className="absolute right-[9px] top-[7px] h-[6px] w-[6px] rounded-full bg-amber ring-2 ring-surface"
                          aria-label={`${badge} waiting`}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>

          {views && views.length > 0 && !collapsed && (
            <div className="flex flex-col gap-0.5">
              <div className="micro-tight px-[10px] pb-1.5">Library</div>
              {views.map((view) => (
                <NavLink
                  key={view.key}
                  to={`/documents?view=${view.key}`}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-[9px] rounded-nav px-[10px] py-1.5 text-base+ no-underline transition-colors",
                      isActive && location.search.includes(view.key)
                        ? "bg-surface-sunken font-bold text-ink"
                        : "font-medium text-muted-strong hover:bg-surface-subtle hover:text-ink",
                    ].join(" ")
                  }
                >
                  {/* A rounded square, not a circle — as in the prototype. */}
                  <span
                    className="h-[6px] w-[6px] shrink-0 rounded-[2px]"
                    style={{ background: VIEW_DOT[view.key] ?? view.dotColour ?? "#CFC8CD" }}
                    aria-hidden
                  />
                  <span className="flex-1 truncate">{view.label}</span>
                  <span className="text-sm text-muted-soft">{view.count}</span>
                </NavLink>
              ))}
            </div>
          )}

          </div>
        </nav>

        {/* --------------------------------------------------------- content */}
        {/* The breadcrumb band that sat here is gone. It spent 41px on every
            page to say "Compendium / Ingestion" when the sidebar already
            highlights the screen you are on. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* The `key` belongs on the element that owns the scrolling, and must
              stay there. Remounting the scroll container is the only thing that
              resets scroll position between routes — there is no
              ScrollRestoration and nothing calls window.scrollTo — so moving
              the key to the padding wrapper below would leave every screen
              inheriting the last one's scrollTop.

              `animate-in` sits on the inner wrapper for the same reason in
              reverse: inside the keyed subtree, so it replays on navigation.
              It also keeps the transform off the scrollport, where it would
              become the containing block for any future fixed-position child.

              `min-h-full` is padding-inclusive under the global border-box, so
              a short page gets no phantom overflow from its own padding.

              `min-h-full` is a floor on an auto height, so a screen wanting
              the exact pane height cannot get it from `flex-1` alone --
              that resolves against content, which is how the assistant's
              rail once came to dictate a 3930px row. So one route opts into
              a definite height instead: `h-full` on the wrapper and
              `overflow-hidden` on the scrollport. The assistant is a
              transcript beside a rail, and both must scroll on their own --
              a shared scroller left a 1700px band of empty rail beside the
              conversation as soon as an answer carried a page image.

              Only from `lg`, which is where the two columns stop wrapping.
              Below it they stack, a definite height would clip the lower
              one, and the pane scrolls as every other screen does. */}
          <div
            key={location.pathname}
            className={`min-h-0 flex-1 overscroll-contain ${
              paneIsFixedHeight
                ? "overflow-y-auto lg:overflow-hidden"
                : "overflow-y-auto"
            }`}
            // With no document scroll, a keyboard user who has not focused
            // anything inside cannot page this pane in Firefox without it.
            tabIndex={0}
          >
            <div
              className={`animate-in px-6 pb-6 pt-[22px] ${
                paneIsFixedHeight ? "min-h-full lg:h-full" : "min-h-full"
              }`}
            >
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useDismiss(() => setOpen(false));
  const seen = useRef(false);

  const { data, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<NotificationRow[]>("/notifications", { limit: 10 }),
    refetchInterval: 60_000,
  });

  const unread = (data ?? []).filter((n) => !n.readAt).length;

  useEffect(() => {
    if (!open || seen.current || unread === 0) return;
    seen.current = true;
    void api.post("/notifications/read").then(() => refetch());
  }, [open, unread, refetch]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-[5px] text-night-100 transition-colors hover:text-pearl"
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute right-[3px] top-[3px] h-1.5 w-1.5 rounded-full bg-accent" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[34px] z-40 w-80 animate-inMenu overflow-hidden rounded-card border border-line bg-surface text-ink shadow-menu">
          <div className="border-b border-line-soft px-[14px] py-[11px]">
            <span className="micro">Notifications</span>
          </div>
          {(data ?? []).length === 0 ? (
            <p className="px-[14px] py-5 text-center text-base+ text-muted">
              Nothing to report.
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {(data ?? []).map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      setOpen(false);
                      if (item.targetKind === "document" && item.targetId) {
                        navigate(`/documents/${item.targetId}`);
                      } else if (item.targetKind === "view" && item.targetId) {
                        navigate(`/documents?view=${item.targetId}`);
                      }
                    }}
                    className="flex w-full items-start gap-[10px] border-b border-line-soft px-[14px] py-[11px] text-left last:border-0 hover:bg-surface-subtle"
                  >
                    <span
                      className="mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full"
                      style={{ background: item.dotColour }}
                      aria-hidden
                    />
                    <span className="min-w-0">
                      <span className="block text-base+ leading-[1.5]">{item.text}</span>
                      <span className="mt-0.5 block text-sm text-muted-soft">
                        {timeAgo(item.createdAt)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
